import React, { useState } from "react";
import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { ZONE_BENEFITS } from "../lib/constants";

/**
 * ZoneGuideEditor — lets the coach customise the page-3 zone guide per test.
 * Expects `value` as Array<{label, points: string[], howTo: string}> and an onChange.
 * If `value` is empty/missing the component falls back to the global default.
 */
export default function ZoneGuideEditor({ value, onChange }) {
  const guides = Array.isArray(value) && value.length === 5 ? value : ZONE_BENEFITS;
  const [openIdx, setOpenIdx] = useState(null);

  const update = (idx, field, v) => {
    const next = guides.map((g, i) => (i === idx ? { ...g, [field]: v } : g));
    onChange(next);
  };

  const updatePoints = (idx, text) => {
    const points = text.split("\n").map((s) => s.trimEnd()).filter((s) => s.length > 0);
    update(idx, "points", points);
  };

  const resetOne = (idx) => {
    const next = guides.map((g, i) => (i === idx ? { ...ZONE_BENEFITS[idx] } : g));
    onChange(next);
  };

  const resetAll = () => onChange(ZONE_BENEFITS.map((g) => ({ ...g, points: [...g.points] })));

  return (
    <div className="w-full" data-testid="zone-guide-editor">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)] font-semibold">
            Zone Guide (page 3 of report)
          </div>
          <div className="font-serif-display text-xl text-[var(--tir-ink)] mt-0.5">
            Customise zone notes for this athlete
          </div>
        </div>
        <button
          type="button"
          onClick={resetAll}
          data-testid="reset-all-zone-guides-btn"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
        >
          <RotateCcw size={13} /> Reset all to default
        </button>
      </div>

      <div className="border border-[var(--tir-border)] bg-white divide-y divide-[var(--tir-border)]">
        {guides.map((g, idx) => {
          const open = openIdx === idx;
          return (
            <div key={idx} data-testid={`zone-guide-${idx}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(open ? null : idx)}
                data-testid={`zone-guide-${idx}-toggle`}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--tir-ivory)] transition-colors"
              >
                <span className="flex items-center gap-2">
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="font-serif-display text-base text-[var(--tir-ink)]">
                    {g.label || `Zone ${idx + 1}`}
                  </span>
                </span>
                <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                  {g.points?.length || 0} bullets
                </span>
              </button>
              {open && (
                <div className="px-4 pb-5 pt-1 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                      Label
                    </label>
                    <input
                      type="text"
                      value={g.label || ""}
                      onChange={(e) => update(idx, "label", e.target.value)}
                      data-testid={`zone-guide-${idx}-label`}
                      className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 text-sm font-mono-tir focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                      Benefits (one per line)
                    </label>
                    <textarea
                      value={(g.points || []).join("\n")}
                      onChange={(e) => updatePoints(idx, e.target.value)}
                      rows={6}
                      data-testid={`zone-guide-${idx}-points`}
                      className="w-full bg-[var(--tir-ivory)] border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] p-2.5 text-sm focus:outline-none leading-relaxed"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                      How to use it
                    </label>
                    <textarea
                      value={g.howTo || ""}
                      onChange={(e) => update(idx, "howTo", e.target.value)}
                      rows={6}
                      data-testid={`zone-guide-${idx}-howto`}
                      className="w-full bg-[var(--tir-ivory)] border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] p-2.5 text-sm focus:outline-none leading-relaxed"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="button"
                      onClick={() => resetOne(idx)}
                      data-testid={`zone-guide-${idx}-reset`}
                      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
                    >
                      <RotateCcw size={12} /> Reset this zone
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[var(--tir-muted)] mt-2">
        Edits here only affect this athlete's report. Click a zone row to expand and edit.
      </p>
    </div>
  );
}
